import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ExternalCall} from '../../models/external-call.model';
import {FormControl, FormGroup} from '@angular/forms';
import {ExternalCallService} from '../../services/external-call.service';
import {Subscription} from 'rxjs';
import {SharedModule} from '../../components/shared.module';
import {RouterLink} from '@angular/router';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {MatCard, MatCardContent} from '@angular/material/card';
import {PageTitleComponent} from '../../components/page-title/page-title.component';
import {ExternalCallStat} from '../../models/external-call-stat.model';

@Component({
  selector: 'antm-external-calls',
  imports: [
    SharedModule,
    RouterLink,
    MatOption,
    MatSelect,
    MatCard,
    MatCardContent,
    PageTitleComponent
  ],
  templateUrl: './external-calls.component.html',
  styleUrl: './external-calls.component.scss',
})
export class ExternalCallsComponent implements OnDestroy, OnInit {
  private externalCallService = inject(ExternalCallService);
  private subscriptions: Subscription[] = [];

  filterForm = new FormGroup({
    search: new FormControl('', {nonNullable: true}),
    type: new FormControl<string>('ALL', {nonNullable: true}),
    status: new FormControl<string>('ALL', {nonNullable: true}),
  });

  stats = signal<ExternalCallStat | null>(null);

  searchTerm = signal('');
  typeFilter = signal<string>('ALL');
  statusFilter = signal<string>('ALL');
  selectedCall = signal<ExternalCall | null>(null);
  showPayloadRaw = signal(false);

  allCalls = this.externalCallService.getCalls;

  externalCalls = signal<ExternalCall[]>([]);
  filteredCalls = computed(() => {
    const list = this.allCalls();
    const search = this.searchTerm().trim().toLowerCase();
    const type = this.typeFilter();
    const status = this.statusFilter();

    return list.filter(call => {
      const matchSearch =
        !search ||
        call.id.toLowerCase().includes(search) ||
        call.serviceName.toLowerCase().includes(search) ||
        call.route.toLowerCase().includes(search) ||
        call.httpStatus.toString().includes(search) ||
        (call.errorMessage && call.errorMessage.toLowerCase().includes(search));

      const matchType = type === 'ALL' || call.type === type;
      const matchStatus = status === 'ALL' || call.status === status;

      return matchSearch && matchType && matchStatus;
    });
  });





  ngOnInit(): void {
    this.externalCallService.getAll().subscribe(any => {
      this.externalCalls.set(any.content);
      this.stats.set(any.stats);
      console.log('ExternalCalls', any)
    })
  }


  availableTypes: {
    type: 'PAYMENT' | 'SMS' | 'GPS' | 'MAPS' | 'VERIFICATION' | 'EMAIL';
    label: string;
    icon: string;
  }[] = [
    {type: 'PAYMENT', label: 'Wave / OM Payment', icon: '💳'},
    {type: 'SMS', label: 'Orange SMS API', icon: '💬'},
    {type: 'GPS', label: 'GPS Fleet Link', icon: '🛰️'},
    {type: 'MAPS', label: 'Google Maps Route', icon: '🗺️'},
    {type: 'VERIFICATION', label: 'Registry Verification', icon: '👤'},
    {type: 'EMAIL', label: 'Sendgrid Automated Email', icon: '✉️'},
  ];

  constructor() {
    this.subscriptions.push(
      this.filterForm.controls.search.valueChanges.subscribe(v => this.searchTerm.set(v)),
      this.filterForm.controls.type.valueChanges.subscribe(v => this.typeFilter.set(v)),
      this.filterForm.controls.status.valueChanges.subscribe(v => this.statusFilter.set(v)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  selectCall(call: ExternalCall): void {
    this.selectedCall.set(call);
    this.showPayloadRaw.set(false);
  }

  closeDrawer(): void {
    this.selectedCall.set(null);
  }

  getTypeBadgeClass(type: string): string {
    return `type-badge type-badge--${type.toLowerCase()}`;
  }

  getStatusClass(status: string): string {
    return `ec-status-badge ec-status-badge--${status.toLowerCase()}`;
  }

  getLatencyClass(duration: number): string {
    if (duration < 250) return 'latency latency--fast';
    if (duration < 600) return 'latency latency--medium';
    return 'latency latency--slow';
  }

  getFormatEntityRoute(type: string, id: string): string {
    switch (type.toLowerCase()) {
      case 'sale':
        return `/sales/${id}`;
      case 'passenger':
        return `/passengers/${id}`;
      case 'employee':
        return `/employees/${id}`;
      case 'bus':
        return `/buses/${id}`;
      case 'traject':
        return `/trajects/${id}`;
      default:
        return '';
    }
  }

  getStatus(call: ExternalCall): string {
    if (call.httpStatus >= 200 && call.httpStatus < 400) {
      return 'SUCCESS';
    }
    return 'FAILED';
  }
}
